/**
 * Browser and WebView Detection Utilities
 * Detects in-app browsers and WebViews to prevent OAuth issues
 */

/**
 * Detect if the current browser is a WebView or in-app browser
 * @returns {Object} Detection result with browser info
 */
export const detectBrowserEnvironment = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for common in-app browser patterns
  const isInstagram = /Instagram/i.test(userAgent);
  const isFacebook = /FBAN|FBAV/i.test(userAgent);
  const isTwitter = /Twitter/i.test(userAgent);
  const isLinkedIn = /LinkedInApp/i.test(userAgent);
  const isTikTok = /TikTok/i.test(userAgent);
  const isSnapchat = /Snapchat/i.test(userAgent);
  const isWeChat = /MicroMessenger/i.test(userAgent);
  const isLine = /Line/i.test(userAgent);
  const isTelegram = /Telegram/i.test(userAgent);
  
  // Generic WebView detection
  const isWebView = /wv|WebView/i.test(userAgent);
  
  // iOS WebView detection
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
  
  // Android WebView detection
  const isAndroidWebView = /Android.*Version\/[\d.]+.*Chrome\/[\d.]+.*Mobile/i.test(userAgent) && 
                           /Version\/[\d.]+.*Chrome\/[\d.]+.*Mobile/i.test(userAgent);
  
  const isInAppBrowser = isInstagram || isFacebook || isTwitter || isLinkedIn || 
                         isTikTok || isSnapchat || isWeChat || isLine || 
                         isTelegram || isWebView || isIOSWebView || isAndroidWebView;
  
  // Determine which specific browser
  let browserName = 'Unknown';
  if (isInstagram) browserName = 'Instagram';
  else if (isFacebook) browserName = 'Facebook';
  else if (isTwitter) browserName = 'Twitter';
  else if (isLinkedIn) browserName = 'LinkedIn';
  else if (isTikTok) browserName = 'TikTok';
  else if (isSnapchat) browserName = 'Snapchat';
  else if (isWeChat) browserName = 'WeChat';
  else if (isLine) browserName = 'Line';
  else if (isTelegram) browserName = 'Telegram';
  else if (isIOSWebView) browserName = 'iOS WebView';
  else if (isAndroidWebView) browserName = 'Android WebView';
  else if (isWebView) browserName = 'WebView';
  
  return {
    isInAppBrowser,
    browserName,
    isInstagram,
    isFacebook,
    isTwitter,
    isLinkedIn,
    isTikTok,
    isSnapchat,
    isWeChat,
    isLine,
    isTelegram,
    isIOSWebView,
    isAndroidWebView,
    isWebView,
    userAgent,
  };
};

/**
 * Get instructions for opening in default browser
 * @param {string} browserName - Name of the detected browser
 * @returns {string} Instructions for the user
 */
export const getBrowserOpenInstructions = (browserName) => {
  const instructions = {
    'Instagram': 'Tap the three dots (...) in the top-right corner and select "Open in Browser"',
    'Facebook': 'Tap the three dots (...) menu and select "Open in External Browser"',
    'Twitter': 'Tap the "Share" icon and select "Open in Browser"',
    'LinkedIn': 'Tap the three dots (...) menu and select "Open in Browser"',
    'TikTok': 'Tap the three dots (...) menu and select "Open in Browser"',
    'Snapchat': 'Tap the three dots (...) menu and select "Open in Browser"',
    'WeChat': 'Tap the three dots (...) menu in top-right and select "Open in Browser"',
    'Line': 'Tap the three dots (...) menu and select "Open in Browser"',
    'Telegram': 'Tap the three dots (...) menu and select "Open in Browser"',
    'iOS WebView': 'Please copy this URL and open it in Safari or Chrome',
    'Android WebView': 'Please copy this URL and open it in Chrome or your default browser',
    'WebView': 'Please open this page in your device\'s default browser',
  };
  
  return instructions[browserName] || 'Please open this page in your default browser (Safari, Chrome, Firefox, etc.)';
};

/**
 * Check if OAuth is safe to use in current environment
 * @returns {boolean} Whether OAuth can be safely initiated
 */
export const isOAuthSafe = () => {
  const { isInAppBrowser } = detectBrowserEnvironment();
  return !isInAppBrowser;
};

/**
 * Copy current URL to clipboard
 * @returns {Promise<boolean>} Success status
 */
export const copyCurrentURLToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href);
    return true;
  } catch (error) {
    console.error('Failed to copy URL:', error);
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError);
      return false;
    }
  }
};

export default {
  detectBrowserEnvironment,
  getBrowserOpenInstructions,
  isOAuthSafe,
  copyCurrentURLToClipboard,
};
